import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trophy, Mail, Lock, Loader2, Eye, EyeOff, Sun, Moon, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { API } from '../../utils/api';

// Google Icon Component
const GoogleIcon = () => (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
);

export default function LoginPage() {
    const navigate = useNavigate();
    const { loginWithEmail, loginWithGoogle } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [step, setStep] = useState('email'); // 'email', 'password', 'unverified', 'google_only', 'not_found'
    const [loading, setLoading] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleContinueEmail = async (e) => {
        e.preventDefault();
        if (!formData.email) return;
        setCheckingEmail(true);

        try {
            const response = await axios.post(`${API}/auth/check-email`, {
                email: formData.email
            });
            const status = response.data.status;
            if (status === 'has_password') {
                setStep('password');
            } else if (status === 'unverified' || status === 'no_password') {
                setStep('unverified');
            } else if (status === 'google_only') {
                setStep('google_only');
            } else if (status === 'not_found') {
                setStep('not_found');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al verificar el correo';
            toast.error(message);
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await loginWithEmail(formData.email, formData.password);
            toast.success('¡Bienvenido!');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al iniciar sesión';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        try {
            await loginWithGoogle();
        } catch (error) {
            toast.error('Error al conectar con Google');
            setGoogleLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5 relative">
            {/* Theme toggle */}
            <button
                onClick={toggleTheme}
                className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 active:scale-90"
                data-testid="login-theme-toggle"
            >
                {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <Trophy className="w-10 h-10 text-primary animate-pulse" />
                        <span className="font-heading text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent">POLLA REMAKIA</span>
                    </Link>
                </div>

                <Card className="border-border/50 shadow-xl backdrop-blur-sm bg-card/95">
                    <div>
                        {step === 'email' && (
                            <div>
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-2xl font-bold">Iniciar Sesión</CardTitle>
                                    <CardDescription>
                                        Ingresa tu correo electrónico para continuar
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleContinueEmail} className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Correo electrónico</label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type="email"
                                                    placeholder="tu@email.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    className="pl-10"
                                                    required
                                                    data-testid="login-email"
                                                />
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="w-full bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/95 hover:to-emerald-600/95"
                                            disabled={checkingEmail}
                                            data-testid="login-continue"
                                        >
                                            {checkingEmail ? (
                                                <span className="contents">
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Verificando...
                                                </span>
                                            ) : (
                                                'Continuar'
                                            )}
                                        </Button>

                                        <div className="relative my-4">
                                            <div className="absolute inset-0 flex items-center">
                                                <span className="w-full border-t border-border" />
                                            </div>
                                            <div className="relative flex justify-center text-xs uppercase">
                                                <span className="bg-card px-2 text-muted-foreground">o continúa con</span>
                                            </div>
                                        </div>

                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full hover:bg-secondary/50"
                                            onClick={handleGoogleLogin}
                                            disabled={googleLoading}
                                            data-testid="google-login-btn"
                                        >
                                            {googleLoading ? (
                                                <span className="contents">
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Conectando...
                                                </span>
                                            ) : (
                                                <span className="contents">
                                                    <GoogleIcon />
                                                    Google
                                                </span>
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                                <CardFooter className="flex flex-col gap-4">
                                    <div className="text-center text-sm text-muted-foreground">
                                        ¿No tienes cuenta?{' '}
                                        <Link 
                                            to="/register" 
                                            className="text-primary hover:underline font-medium"
                                            data-testid="register-link"
                                        >
                                            Regístrate aquí
                                        </Link>
                                    </div>
                                </CardFooter>
                            </div>
                        )}

                        {step === 'password' && (
                            <div>
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-2xl font-bold">Ingresa tu contraseña</CardTitle>
                                    <CardDescription className="flex flex-col items-center gap-1">
                                        <span>Conectado como <strong className="text-foreground">{formData.email}</strong></span>
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                setStep('email');
                                                setFormData(prev => ({ ...prev, password: '' }));
                                            }}
                                            className="text-xs text-primary hover:underline font-medium"
                                        >
                                            ¿Cambiar de correo?
                                        </button>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="text-sm font-medium">Contraseña</label>
                                                <Link 
                                                    to="/forgot-password" 
                                                    className="text-xs text-primary hover:underline"
                                                    data-testid="forgot-password-link"
                                                >
                                                    ¿Olvidaste tu contraseña?
                                                </Link>
                                            </div>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="••••••••"
                                                    value={formData.password}
                                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                    className="pl-10 pr-10"
                                                    required
                                                    autoFocus
                                                    data-testid="login-password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="w-full bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/95 hover:to-emerald-600/95"
                                            disabled={loading}
                                            data-testid="login-submit"
                                        >
                                            {loading ? (
                                                <span className="contents">
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Ingresando...
                                                </span>
                                            ) : (
                                                'Iniciar Sesión'
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                            </div>
                        )}

                        {step === 'unverified' && (
                            <CardContent className="pt-8 pb-8 text-center">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/20 opacity-75"></span>
                                    <Mail className="w-8 h-8 text-primary relative z-10" />
                                </div>
                                <h2 className="text-2xl font-bold mb-2 text-foreground">¡Revisa tu correo!</h2>
                                <p className="text-muted-foreground mb-4">
                                    Revisa tu correo y valídalo para continuar. Hemos enviado un correo de activación a:<br />
                                    <span className="font-semibold text-foreground block mt-1">{formData.email}</span>
                                </p>
                                
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-500 mb-6 text-left">
                                    <strong>Importante:</strong> Si no lo recibes en un minuto, revisa tu carpeta de <strong>Correo no deseado (Spam)</strong> o Promociones.
                                </div>

                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setStep('email');
                                        setFormData(prev => ({ ...prev, password: '' }));
                                    }}
                                    className="w-full hover:bg-secondary/50"
                                >
                                    Volver a Iniciar Sesión
                                </Button>
                            </CardContent>
                        )}

                        {step === 'google_only' && (
                            <CardContent className="pt-8 pb-8 text-center">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <GoogleIcon />
                                </div>
                                <h2 className="text-2xl font-bold mb-2">Cuenta de Google</h2>
                                <p className="text-muted-foreground mb-6">
                                    La cuenta con el correo <span className="font-semibold text-foreground">{formData.email}</span> usa Google para iniciar sesión.
                                </p>
                                
                                <Button
                                    type="button"
                                    className="w-full mb-4 bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/95 hover:to-emerald-600/95"
                                    onClick={handleGoogleLogin}
                                    disabled={googleLoading}
                                >
                                    {googleLoading ? (
                                        <span className="contents">
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Conectando...
                                        </span>
                                    ) : (
                                        <span className="contents">
                                            <GoogleIcon />
                                            Iniciar sesión con Google
                                        </span>
                                    )}
                                </Button>

                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setStep('email');
                                        setFormData(prev => ({ ...prev, password: '' }));
                                    }}
                                    className="w-full hover:bg-secondary/50"
                                >
                                    Usar otro correo
                                </Button>
                            </CardContent>
                        )}

                        {step === 'not_found' && (
                            <CardContent className="pt-8 pb-8 text-center">
                                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Trophy className="w-8 h-8 text-red-500" />
                                </div>
                                <h2 className="text-2xl font-bold mb-2">Correo no registrado</h2>
                                <p className="text-muted-foreground mb-6">
                                    No encontramos una cuenta con el correo <span className="font-semibold text-foreground">{formData.email}</span>. ¿Deseas registrarte?
                                </p>
                                
                                <Button
                                    type="button"
                                    className="w-full mb-4 bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/95 hover:to-emerald-600/95"
                                    onClick={() => navigate(`/register?email=${encodeURIComponent(formData.email)}`)}
                                >
                                    Registrarse con este correo
                                </Button>

                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setStep('email');
                                        setFormData(prev => ({ ...prev, password: '' }));
                                    }}
                                    className="w-full hover:bg-secondary/50"
                                >
                                    Intentar con otro correo
                                </Button>
                            </CardContent>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}

